import { NurseLayout } from "@/components/layout/nurse-layout";
import { useGetCondition, getGetConditionQueryKey, useListScenarios, getListScenariosQueryKey } from "@workspace/api-client-react";
import { LoadingScreen } from "@/components/ui/loading-screen";
import { useParams, Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  ArrowLeft, Stethoscope, Pill, Microscope, Clipboard,
  FlaskConical, BookOpen, HeartPulse, ShieldCheck, ListChecks, ChevronRight,
  AlertTriangle, MessageCircle, CalendarClock, ArrowUpCircle,
  CheckCircle2, Clock, Zap, Lightbulb, Microscope, Camera, TrendingUp, Hand, ExternalLink
} from "lucide-react";
import { UkHealthDisclaimer } from "@/components/ui/uk-health-disclaimer";
import { BackButton } from "@/components/ui/back-button";
import { useSearchParam } from "@/hooks/use-search-param";

type ClinicalFeature = { feature: string; detail: string };
type Classification = { system: string; criteria: string[] };
type Investigation = { category: string; tests: string[] };
type RedFlag = { flag: string; action: string };
type FollowUpGuidance = { frequency: string; monitoring: string[]; goals: string[] };
type EscalationPathway = { trigger: string; action: string; urgency: "routine" | "urgent" | "emergency" };

const conditionTypeLabel: Record<string, { label: string; color: string }> = {
  inflammatory_arthritis: { label: "Inflammatory Arthritis", color: "bg-blue-50 text-blue-700 border-blue-200" },
  connective_tissue: { label: "Connective Tissue Disorders", color: "bg-purple-50 text-purple-700 border-purple-200" },
  crystal_arthropathy: { label: "Crystal Arthropathy", color: "bg-amber-50 text-amber-700 border-amber-200" },
  vasculitis: { label: "Vasculitis", color: "bg-rose-50 text-rose-700 border-rose-200" },
  other: { label: "Other", color: "bg-slate-50 text-slate-700 border-slate-200" },
};

const urgencyConfig: Record<string, { label: string; bg: string; text: string; border: string; dot: string; icon: React.ComponentType<{ className?: string }> }> = {
  emergency: {
    label: "Emergency",
    bg: "bg-red-50",
    text: "text-red-800",
    border: "border-red-200",
    dot: "bg-red-500",
    icon: Zap,
  },
  urgent: {
    label: "Urgent",
    bg: "bg-amber-50",
    text: "text-amber-800",
    border: "border-amber-200",
    dot: "bg-amber-500",
    icon: Clock,
  },
  routine: {
    label: "Routine",
    bg: "bg-teal-50",
    text: "text-teal-800",
    border: "border-teal-200",
    dot: "bg-teal-500",
    icon: CheckCircle2,
  },
};

function ConditionTabs({
  hasNurseGuidance,
  children,
}: {
  hasNurseGuidance: boolean;
  children: (value: string, onValueChange: (v: string) => void) => React.ReactNode;
}) {
  const defaultTab = hasNurseGuidance ? "nurse-guidance" : "overview";
  const [tab, setTab] = useSearchParam("tab", defaultTab);
  return <>{children(tab || defaultTab, setTab)}</>;
}

export default function NurseConditionDetail() {
  const { id } = useParams();
  const { data: condition, isLoading, error } = useGetCondition(Number(id), {
    query: { enabled: !!id, queryKey: getGetConditionQueryKey(Number(id)) }
  });

  const { data: allScenarios } = useListScenarios({}, {
    query: { queryKey: getListScenariosQueryKey({}) }
  });
  const relatedScenarios = condition
    ? (allScenarios ?? []).filter(s => s.relatedConditionIds?.includes(condition.id))
    : [];

  if (isLoading) return <NurseLayout title="Condition Detail"><LoadingScreen /></NurseLayout>;
  if (error || !condition) return <NurseLayout title="Condition Detail"><div className="p-8 text-slate-500">Condition not found</div></NurseLayout>;

  const clinicalFeatures = (condition.clinicalFeatures ?? []) as ClinicalFeature[];
  const classifications = (condition.classifications ?? []) as Classification[];
  const investigations = (condition.investigations ?? []) as Investigation[];
  const redFlags = (condition.redFlags ?? []) as RedFlag[];
  const counsellingPoints = (condition.counsellingPoints ?? []) as string[];
  const followUpGuidance = condition.followUpGuidance as FollowUpGuidance | null | undefined;
  const escalationPathways = (condition.escalationPathways ?? []) as EscalationPathway[];
  const typeInfo = condition.conditionType ? conditionTypeLabel[condition.conditionType] ?? conditionTypeLabel.other : null;

  const hasNurseGuidance = !!(
    redFlags.length > 0 ||
    counsellingPoints.length > 0 ||
    followUpGuidance ||
    escalationPathways.length > 0
  );

  const hasAdvancedData = !!(
    condition.aetiopathogenesis ||
    clinicalFeatures.length > 0 ||
    classifications.length > 0 ||
    investigations.length > 0 ||
    condition.managementMedical ||
    condition.managementNursing ||
    hasNurseGuidance
  );

  return (
    <NurseLayout title={condition.name}>
      <BackButton href="/nurse/conditions">Back to Conditions</BackButton>

      <UkHealthDisclaimer variant="nurse" compact />
      <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-2xl px-5 py-3 text-xs text-amber-800 mt-2 mb-4">
        <svg className="h-3.5 w-3.5 text-amber-600 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
        <p>Treatment pathways reflect national NICE/BSR guidelines. Your NHS Trust or ICB may have local policies, formularies, or shared care agreements that differ. Always follow your local protocols.</p>
      </div>

      {/* Header */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8 mb-6">
        <div className="flex flex-wrap items-start gap-3 mb-2">
          <h1 className="text-3xl font-bold text-slate-900">{condition.name}</h1>
          {typeInfo && (
            <span className={`mt-1 text-xs font-semibold px-3 py-1 rounded-full border ${typeInfo.color}`}>
              {typeInfo.label}
            </span>
          )}
        </div>
        {condition.simpleName && condition.simpleName !== condition.name && (
          <p className="text-slate-500 mb-3">Also known as: {condition.simpleName}</p>
        )}
        {condition.prevalence && (
          <p className="text-sm text-slate-600 bg-slate-50 rounded-lg px-4 py-2 border border-slate-100 inline-block mb-4">
            <span className="font-semibold text-slate-700">Prevalence:</span> {condition.prevalence}
          </p>
        )}
        <div className="border-t border-slate-100 pt-4 mt-2">
          <p className="text-slate-700 leading-relaxed">{condition.nurseDescription}</p>
        </div>
      </div>

      {hasAdvancedData ? (
        <ConditionTabs hasNurseGuidance={hasNurseGuidance}>
          {(value, onValueChange) => (
        <Tabs value={value} onValueChange={onValueChange} className="space-y-4">
          <TabsList className="flex-wrap h-auto gap-1 bg-slate-100 p-1 sticky top-16 z-10 shadow-sm">
            <TabsTrigger value="overview" className="gap-2">
              <Stethoscope className="h-4 w-4" /> Overview
            </TabsTrigger>
            {hasNurseGuidance && (
              <TabsTrigger value="nurse-guidance" className="gap-2 data-[state=active]:bg-red-600 data-[state=active]:text-white">
                <AlertTriangle className="h-4 w-4" /> Nurse Guidance
              </TabsTrigger>
            )}
            {condition.aetiopathogenesis && (
              <TabsTrigger value="aetio" className="gap-2">
                <FlaskConical className="h-4 w-4" /> Aetiopathogenesis
              </TabsTrigger>
            )}
            {clinicalFeatures.length > 0 && (
              <TabsTrigger value="features" className="gap-2">
                <HeartPulse className="h-4 w-4" /> Clinical Features
              </TabsTrigger>
            )}
            {classifications.length > 0 && (
              <TabsTrigger value="classification" className="gap-2">
                <ListChecks className="h-4 w-4" /> Classification
              </TabsTrigger>
            )}
            {investigations.length > 0 && (
              <TabsTrigger value="investigations" className="gap-2">
                <Microscope className="h-4 w-4" /> Investigations
              </TabsTrigger>
            )}
            {(condition.managementMedical || condition.managementNursing) && (
              <TabsTrigger value="management" className="gap-2">
                <Clipboard className="h-4 w-4" /> Management
              </TabsTrigger>
            )}
            {(condition as any).pathology && (
              <TabsTrigger value="pathology" className="gap-2">
                <Microscope className="h-4 w-4" /> Pathology
              </TabsTrigger>
            )}
            {(condition as any).imaging && (
              <TabsTrigger value="imaging" className="gap-2">
                <Camera className="h-4 w-4" /> Imaging
              </TabsTrigger>
            )}
            {(condition as any).prognosis && (
              <TabsTrigger value="prognosis" className="gap-2">
                <TrendingUp className="h-4 w-4" /> Prognosis
              </TabsTrigger>
            )}
            {(condition as any).otPtInterventions && (
              <TabsTrigger value="ot-pt" className="gap-2">
                <Hand className="h-4 w-4" /> OT / PT
              </TabsTrigger>
            )}
            {((condition as any).charityLinks?.length > 0) && (
              <TabsTrigger value="charities" className="gap-2">
                <ExternalLink className="h-4 w-4" /> Charities
              </TabsTrigger>
            )}
            {(condition as any).sources && (
              <TabsTrigger value="sources" className="gap-2">
                <BookOpen className="h-4 w-4" /> Sources
              </TabsTrigger>
            )}
          </TabsList>

          {/* OVERVIEW TAB */}
          <TabsContent value="overview">
            <div className="grid gap-6 md:grid-cols-3">
              <div className="md:col-span-2 space-y-6">
                {condition.keySymptoms && condition.keySymptoms.length > 0 && (
                  <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
                    <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
                      <HeartPulse className="h-5 w-5 text-rose-500" /> Key Symptoms
                    </h3>
                    <div className="grid gap-2 sm:grid-cols-2">
                      {condition.keySymptoms.map(s => (
                        <div key={s} className="flex items-start gap-2 text-sm text-slate-700">
                          <ChevronRight className="h-4 w-4 text-teal-500 mt-0.5 shrink-0" />
                          {s}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {condition.relatedMedications && condition.relatedMedications.length > 0 && (
                  <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
                    <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
                      <Pill className="h-5 w-5 text-indigo-500" /> Related Medications
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {condition.relatedMedications.map(m => (
                        <Badge key={m} variant="outline" className="bg-slate-50 text-slate-700 hover:bg-indigo-50 hover:text-indigo-700 hover:border-indigo-200 transition-colors cursor-default">
                          {m}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-6">
                {condition.niceGuideline && (
                  <div className="bg-teal-900 rounded-2xl shadow-sm p-6 text-white">
                    <h3 className="text-md font-semibold mb-2 flex items-center gap-2">
                      <BookOpen className="h-4 w-4" /> NICE Guideline
                    </h3>
                    <p className="text-sm text-teal-200 mb-4">{condition.niceGuideline}</p>
                    <a href={`https://www.nice.org.uk/guidance/${condition.niceGuideline.toLowerCase()}`} target="_blank" rel="noopener noreferrer">
                      <Button variant="outline" className="w-full bg-teal-800 text-white border-teal-700 hover:bg-teal-700 hover:text-white">
                        View on NICE →
                      </Button>
                    </a>
                  </div>
                )}
                <div className="bg-amber-50 rounded-2xl border border-amber-100 p-5">
                  <h4 className="text-sm font-semibold text-amber-900 mb-2">Patient Explanation</h4>
                  <p className="text-sm text-amber-800 italic leading-relaxed">"{condition.patientDescription}"</p>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* NURSE GUIDANCE TAB */}
          {hasNurseGuidance && (
            <TabsContent value="nurse-guidance">
              <div className="space-y-6">

                {/* Red Flags */}
                {redFlags.length > 0 && (
                  <div className="bg-white rounded-2xl shadow-sm border border-red-200 overflow-hidden">
                    <div className="bg-red-600 px-6 py-4 flex items-center gap-3">
                      <AlertTriangle className="h-6 w-6 text-white" />
                      <div>
                        <h2 className="text-xl font-bold text-white">Red Flags</h2>
                        <p className="text-red-100 text-sm">Clinical warning signs requiring immediate nurse action</p>
                      </div>
                    </div>
                    <div className="divide-y divide-red-100">
                      {redFlags.map((rf, i) => (
                        <div key={i} className="p-5 hover:bg-red-50 transition-colors">
                          <div className="flex items-start gap-3">
                            <div className="h-6 w-6 rounded-full bg-red-100 text-red-700 text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">
                              {i + 1}
                            </div>
                            <div className="flex-1">
                              <p className="font-semibold text-red-900 text-sm mb-2">{rf.flag}</p>
                              <div className="flex items-start gap-2 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                                <ShieldCheck className="h-4 w-4 text-red-600 mt-0.5 shrink-0" />
                                <p className="text-red-800 text-xs leading-relaxed">{rf.action}</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="grid gap-6 lg:grid-cols-2">
                  {/* Counselling Points */}
                  {counsellingPoints.length > 0 && (
                    <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                      <div className="border-b border-slate-100 px-6 py-4 flex items-center gap-3">
                        <div className="bg-indigo-100 p-2 rounded-lg">
                          <MessageCircle className="h-5 w-5 text-indigo-700" />
                        </div>
                        <div>
                          <h2 className="text-lg font-bold text-slate-900">Patient Counselling Points</h2>
                          <p className="text-slate-500 text-xs">Key messages to cover with patients</p>
                        </div>
                      </div>
                      <div className="p-5">
                        <ul className="space-y-3">
                          {counsellingPoints.map((point, i) => (
                            <li key={i} className="flex items-start gap-3 text-sm text-slate-700">
                              <span className="h-5 w-5 rounded-full bg-indigo-100 text-indigo-700 text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">
                                {i + 1}
                              </span>
                              <span className="leading-relaxed">{point}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}

                  {/* Follow-Up Guidance */}
                  {followUpGuidance && (
                    <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                      <div className="border-b border-slate-100 px-6 py-4 flex items-center gap-3">
                        <div className="bg-teal-100 p-2 rounded-lg">
                          <CalendarClock className="h-5 w-5 text-teal-700" />
                        </div>
                        <div>
                          <h2 className="text-lg font-bold text-slate-900">Follow-Up Guidance</h2>
                          <p className="text-slate-500 text-xs">Monitoring schedule and treat-to-target goals</p>
                        </div>
                      </div>
                      <div className="p-5 space-y-5">
                        <div className="bg-teal-50 rounded-xl border border-teal-100 px-4 py-3">
                          <p className="text-xs font-semibold text-teal-700 uppercase tracking-wide mb-1">Frequency</p>
                          <p className="text-teal-900 text-sm font-medium">{followUpGuidance.frequency}</p>
                        </div>

                        {followUpGuidance.monitoring.length > 0 && (
                          <div>
                            <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2">Monitoring Parameters</p>
                            <ul className="space-y-1.5">
                              {followUpGuidance.monitoring.map((item, i) => (
                                <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                                  <span className="h-1.5 w-1.5 rounded-full bg-teal-500 mt-2 shrink-0" />
                                  {item}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {followUpGuidance.goals.length > 0 && (
                          <div>
                            <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2">Treatment Goals</p>
                            <ul className="space-y-2">
                              {followUpGuidance.goals.map((goal, i) => (
                                <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                                  <CheckCircle2 className="h-4 w-4 text-teal-500 mt-0.5 shrink-0" />
                                  {goal}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Escalation Pathways */}
                {escalationPathways.length > 0 && (
                  <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                    <div className="border-b border-slate-100 px-6 py-4 flex items-center gap-3">
                      <div className="bg-amber-100 p-2 rounded-lg">
                        <ArrowUpCircle className="h-5 w-5 text-amber-700" />
                      </div>
                      <div>
                        <h2 className="text-lg font-bold text-slate-900">Escalation Pathways</h2>
                        <p className="text-slate-500 text-xs">When and how to escalate care</p>
                      </div>
                    </div>
                    <div className="p-5">
                      <div className="flex flex-wrap gap-3 mb-4">
                        {(["emergency", "urgent", "routine"] as const).map(level => {
                          const count = escalationPathways.filter(e => e.urgency === level).length;
                          if (count === 0) return null;
                          const cfg = urgencyConfig[level];
                          return (
                            <span key={level} className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded-full border ${cfg.bg} ${cfg.text} ${cfg.border}`}>
                              <span className={`h-1.5 w-1.5 rounded-full ${cfg.dot}`} />
                              {count} {cfg.label}
                            </span>
                          );
                        })}
                      </div>

                      {(["emergency", "urgent", "routine"] as const).map(level => {
                        const pathways = escalationPathways.filter(e => e.urgency === level);
                        if (pathways.length === 0) return null;
                        const cfg = urgencyConfig[level];
                        const Icon = cfg.icon;
                        return (
                          <div key={level} className="mb-4">
                            <div className={`flex items-center gap-2 mb-2`}>
                              <Icon className={`h-4 w-4 ${level === "emergency" ? "text-red-600" : level === "urgent" ? "text-amber-600" : "text-teal-600"}`} />
                              <p className={`text-xs font-bold uppercase tracking-wide ${level === "emergency" ? "text-red-700" : level === "urgent" ? "text-amber-700" : "text-teal-700"}`}>
                                {cfg.label}
                              </p>
                            </div>
                            <div className="space-y-3 pl-6">
                              {pathways.map((ep, i) => (
                                <div key={i} className={`border ${cfg.border} rounded-xl overflow-hidden`}>
                                  <div className={`${cfg.bg} px-4 py-2`}>
                                    <p className={`text-sm font-semibold ${cfg.text}`}>{ep.trigger}</p>
                                  </div>
                                  <div className="px-4 py-3 bg-white">
                                    <div className="flex items-start gap-2">
                                      <ChevronRight className={`h-4 w-4 mt-0.5 shrink-0 ${level === "emergency" ? "text-red-500" : level === "urgent" ? "text-amber-500" : "text-teal-500"}`} />
                                      <p className="text-sm text-slate-700">{ep.action}</p>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </TabsContent>
          )}

          {/* AETIOPATHOGENESIS TAB */}
          {condition.aetiopathogenesis && (
            <TabsContent value="aetio">
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="bg-purple-100 p-2 rounded-lg">
                    <FlaskConical className="h-6 w-6 text-purple-700" />
                  </div>
                  <h2 className="text-2xl font-bold text-slate-900">Aetiopathogenesis</h2>
                </div>
                <div className="prose prose-slate max-w-none">
                  <p className="text-slate-700 leading-relaxed text-base whitespace-pre-wrap">{condition.aetiopathogenesis}</p>
                </div>
              </div>
            </TabsContent>
          )}

          {/* CLINICAL FEATURES TAB */}
          {clinicalFeatures.length > 0 && (
            <TabsContent value="features">
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="bg-rose-100 p-2 rounded-lg">
                    <HeartPulse className="h-6 w-6 text-rose-700" />
                  </div>
                  <h2 className="text-2xl font-bold text-slate-900">Clinical Features</h2>
                </div>
                <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                  {clinicalFeatures.map((f, i) => (
                    <div key={i} className="border border-slate-200 rounded-xl overflow-hidden hover:border-teal-300 hover:shadow-sm transition-all">
                      {(f as ClinicalFeature & { imageUrl?: string }).imageUrl ? (
                        <img
                          src={(f as ClinicalFeature & { imageUrl?: string }).imageUrl}
                          alt={f.feature}
                          className="w-full h-36 object-cover"
                        />
                      ) : (
                        <div className="w-full h-36 bg-slate-100 flex flex-col items-center justify-center gap-2 border-b border-slate-200">
                          <HeartPulse className="h-8 w-8 text-slate-300" />
                          <span className="text-xs text-slate-400">Clinical image</span>
                        </div>
                      )}
                      <div className="p-4">
                        <h4 className="font-semibold text-slate-900 mb-1 flex items-center gap-2 text-sm">
                          <span className="h-5 w-5 rounded-full bg-teal-600 text-white text-xs flex items-center justify-center shrink-0">{i + 1}</span>
                          {f.feature}
                        </h4>
                        <p className="text-slate-600 text-xs leading-relaxed pl-7">{f.detail}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </TabsContent>
          )}

          {/* CLASSIFICATION TAB */}
          {classifications.length > 0 && (
            <TabsContent value="classification">
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="bg-blue-100 p-2 rounded-lg">
                    <ListChecks className="h-6 w-6 text-blue-700" />
                  </div>
                  <h2 className="text-2xl font-bold text-slate-900">Classification Criteria</h2>
                </div>
                <div className="space-y-6">
                  {classifications.map((cls, i) => (
                    <div key={i} className="border border-slate-200 rounded-xl overflow-hidden">
                      <div className="bg-slate-800 text-white px-5 py-3">
                        <h4 className="font-semibold text-sm">{cls.system}</h4>
                      </div>
                      <div className="p-5">
                        <ul className="space-y-2">
                          {cls.criteria.map((c, j) => (
                            <li key={j} className="flex items-start gap-3 text-sm text-slate-700">
                              <ChevronRight className="h-4 w-4 text-blue-500 mt-0.5 shrink-0" />
                              <span>{c}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </TabsContent>
          )}

          {/* INVESTIGATIONS TAB */}
          {investigations.length > 0 && (
            <TabsContent value="investigations">
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="bg-indigo-100 p-2 rounded-lg">
                    <Microscope className="h-6 w-6 text-indigo-700" />
                  </div>
                  <h2 className="text-2xl font-bold text-slate-900">Investigations</h2>
                </div>
                <div className="grid gap-4 md:grid-cols-2">
                  {investigations.map((inv, i) => (
                    <div key={i} className="border border-slate-200 rounded-xl overflow-hidden">
                      <div className="bg-indigo-50 border-b border-indigo-100 px-5 py-3">
                        <h4 className="font-semibold text-indigo-900 text-sm">{inv.category}</h4>
                      </div>
                      <div className="p-5">
                        <ul className="space-y-1.5">
                          {inv.tests.map((t, j) => (
                            <li key={j} className="flex items-start gap-2 text-sm text-slate-700">
                              <span className="h-1.5 w-1.5 rounded-full bg-indigo-400 mt-1.5 shrink-0" />
                              {t}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </TabsContent>
          )}

          {/* MANAGEMENT TAB */}
          {(condition.managementMedical || condition.managementNursing) && (
            <TabsContent value="management">
              <Tabs defaultValue={condition.managementMedical ? "medical" : "nursing"} className="space-y-4">
                <TabsList className="bg-slate-100 p-1">
                  {condition.managementMedical && (
                    <TabsTrigger value="medical" className="gap-2">
                      <Clipboard className="h-4 w-4" /> Medical
                    </TabsTrigger>
                  )}
                  {condition.managementNursing && (
                    <TabsTrigger value="nursing" className="gap-2">
                      <ShieldCheck className="h-4 w-4" /> Nursing
                    </TabsTrigger>
                  )}
                </TabsList>
                {condition.managementMedical && (
                  <TabsContent value="medical">
                    <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8">
                      <div className="flex items-center gap-3 mb-6">
                        <div className="bg-teal-100 p-2 rounded-lg">
                          <Clipboard className="h-6 w-6 text-teal-700" />
                        </div>
                        <h2 className="text-xl font-bold text-slate-900">Medical Management</h2>
                      </div>
                      <div className="text-slate-700 text-sm leading-relaxed whitespace-pre-wrap bg-slate-50 rounded-xl p-5 border border-slate-200">
                        {condition.managementMedical}
                      </div>
                    </div>
                  </TabsContent>
                )}
                {condition.managementNursing && (
                  <TabsContent value="nursing">
                    <div className="bg-teal-50 rounded-2xl border border-teal-100 p-8">
                      <div className="flex items-center gap-3 mb-6">
                        <div className="bg-teal-600 p-2 rounded-lg">
                          <ShieldCheck className="h-6 w-6 text-white" />
                        </div>
                        <h2 className="text-xl font-bold text-teal-900">Nursing Management</h2>
                      </div>
                      <div className="text-teal-800 text-sm leading-relaxed whitespace-pre-wrap">
                        {condition.managementNursing}
                      </div>
                    </div>
                  </TabsContent>
                )}
              </Tabs>
            </TabsContent>
          )}

          <TabsContent value="pathology">
            <div className="bg-white rounded-2xl shadow-sm border border-purple-200 p-8">
              <div className="flex items-center gap-3 mb-4">
                <div className="bg-purple-100 p-2 rounded-lg"><Microscope className="h-6 w-6 text-purple-700" /></div>
                <h2 className="text-xl font-bold text-slate-900">Pathology and Lab Findings</h2>
              </div>
              <div className="text-slate-700 text-sm leading-relaxed whitespace-pre-wrap bg-purple-50 rounded-xl p-5 border border-purple-100">
                {(condition as any).pathology}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="imaging">
            <div className="bg-white rounded-2xl shadow-sm border border-sky-200 p-8">
              <div className="flex items-center gap-3 mb-4">
                <div className="bg-sky-100 p-2 rounded-lg"><Camera className="h-6 w-6 text-sky-700" /></div>
                <h2 className="text-xl font-bold text-slate-900">Imaging</h2>
              </div>
              <div className="text-slate-700 text-sm leading-relaxed whitespace-pre-wrap bg-sky-50 rounded-xl p-5 border border-sky-100">
                {(condition as any).imaging}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="prognosis">
            <div className="bg-white rounded-2xl shadow-sm border border-emerald-200 p-8">
              <div className="flex items-center gap-3 mb-4">
                <div className="bg-emerald-100 p-2 rounded-lg"><TrendingUp className="h-6 w-6 text-emerald-700" /></div>
                <h2 className="text-xl font-bold text-slate-900">Prognosis</h2>
              </div>
              <div className="text-slate-700 text-sm leading-relaxed whitespace-pre-wrap bg-emerald-50 rounded-xl p-5 border border-emerald-100">
                {(condition as any).prognosis}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="ot-pt">
            <div className="bg-white rounded-2xl shadow-sm border border-amber-200 p-8">
              <div className="flex items-center gap-3 mb-4">
                <div className="bg-amber-100 p-2 rounded-lg"><Hand className="h-6 w-6 text-amber-700" /></div>
                <h2 className="text-xl font-bold text-slate-900">OT / PT Interventions</h2>
              </div>
              <div className="text-slate-700 text-sm leading-relaxed whitespace-pre-wrap bg-amber-50 rounded-xl p-5 border border-amber-100">
                {(condition as any).otPtInterventions}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="charities">
            <div className="space-y-4">
              <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                <ExternalLink className="h-5 w-5 text-teal-600" /> UK Charity Resources
              </h2>
              <div className="grid gap-4 sm:grid-cols-2">
                {((condition as any).charityLinks ?? []).map((charity: any) => (
                  <a key={charity.name} href={charity.url} target="_blank" rel="noopener noreferrer"
                    className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 hover:border-teal-400 hover:shadow-md transition-all group">
                    <h3 className="font-semibold text-slate-900 mb-2 group-hover:text-teal-700 flex items-center gap-2">
                      {charity.name} <ExternalLink className="h-3.5 w-3.5 text-slate-400" />
                    </h3>
                    <p className="text-sm text-slate-600">{charity.description}</p>
                  </a>
                ))}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="sources">
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8">
              <div className="flex items-center gap-3 mb-4">
                <div className="bg-slate-100 p-2 rounded-lg"><BookOpen className="h-6 w-6 text-slate-700" /></div>
                <div>
                  <h2 className="text-xl font-bold text-slate-900">Sources and References</h2>
                  <p className="text-xs text-slate-500 mt-1">
                    Last reviewed: {(condition as any).lastReviewedAt ? new Date((condition as any).lastReviewedAt).toLocaleDateString("en-GB", {year:"numeric", month:"long"}) : "N/A"}
                    {(condition as any).reviewedBy ? " | Reviewed by: " + (condition as any).reviewedBy : ""}
                  </p>
                </div>
              </div>
              <div className="text-slate-700 text-sm leading-relaxed whitespace-pre-wrap bg-slate-50 rounded-xl p-5 border border-slate-200">
                {(condition as any).sources}
              </div>
            </div>
          </TabsContent>
        </Tabs>
          )}
        </ConditionTabs>
      ) : (
        /* Fallback for conditions without advanced data */
        <div className="grid gap-6 md:grid-cols-3">
          <div className="md:col-span-2 space-y-6">
            {condition.keySymptoms && condition.keySymptoms.length > 0 && (
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
                <h3 className="text-lg font-semibold text-slate-800 mb-4">Key Symptoms</h3>
                <div className="grid gap-2 sm:grid-cols-2">
                  {condition.keySymptoms.map(s => (
                    <div key={s} className="flex items-start gap-2 text-sm text-slate-700">
                      <ChevronRight className="h-4 w-4 text-teal-500 mt-0.5 shrink-0" />{s}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className="space-y-6">
            {condition.relatedMedications && condition.relatedMedications.length > 0 && (
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
                <h3 className="text-md font-semibold text-slate-800 mb-4">Related Medications</h3>
                <div className="flex flex-wrap gap-2">
                  {condition.relatedMedications.map(m => (
                    <Badge key={m} variant="outline" className="bg-slate-50">{m}</Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* #51 Related Scenarios */}
      {relatedScenarios.length > 0 && (
        <div className="mt-8 mb-2">
          <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
            <Lightbulb className="h-5 w-5 text-amber-500" />
            Related Practice Scenarios
          </h2>
          <div className="grid gap-4 sm:grid-cols-2">
            {relatedScenarios.slice(0, 4).map(s => (
              <Link key={s.id} href={`/nurse/scenarios/${s.id}`}>
                <div className="bg-white rounded-2xl border border-slate-200 p-5 hover:border-amber-300 hover:shadow-md transition-all cursor-pointer group">
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <h3 className="font-semibold text-slate-900 text-sm leading-snug group-hover:text-amber-700 transition-colors">{s.title}</h3>
                    <ChevronRight className="h-4 w-4 text-slate-400 group-hover:text-amber-500 shrink-0 mt-0.5 transition-colors" />
                  </div>
                  {s.description && <p className="text-xs text-slate-500 line-clamp-2 mb-3">{s.description}</p>}
                  {s.difficulty && (
                    <span className={`text-xs font-medium rounded-full px-2.5 py-0.5 ${
                      s.difficulty === "advanced" ? "bg-rose-100 text-rose-700" :
                      s.difficulty === "intermediate" ? "bg-amber-100 text-amber-700" :
                      "bg-green-100 text-green-700"
                    }`}>
                      {s.difficulty.charAt(0).toUpperCase() + s.difficulty.slice(1)}
                    </span>
                  )}
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </NurseLayout>
  );
}
